import { type OrderSource, track } from '@/lib/analytics'
import { tradeVenueName } from '@/lib/analytics-format'
import { Denomination, denominationToJSON, OrderType } from '@/types/generated/common/trading'
import type { TradeVenue } from '@/types/generated/common/types'
import type { NewOrderParams } from '@/types/generated/execution'

/** Stable analytics string for a proto order type (never the raw number). */
export function orderTypeName(orderType: OrderType): string {
  switch (orderType) {
    case OrderType.ORDER_TYPE_MARKET:
      return 'market'
    case OrderType.ORDER_TYPE_LIMIT:
      return 'limit'
    case OrderType.ORDER_TYPE_LIQUIDATION:
      return 'liquidation'
    default:
      return 'unknown'
  }
}

export interface AnalyticsOrderItem {
  accountId: string
  symbol: string
  quantity: string
  denomination: Denomination
}

type AnalyticsAccount = { accountId: string; tradeVenue: TradeVenue }

/**
 * One `Submit Order` per item. Orders carry no exchange (routing is
 * server-derived from the account), so it is resolved from the account's trade
 * venue. `size` is the requested quantity as entered, in the unit given by
 * `size_denomination` (base/quote).
 */
export function trackOrderItems(
  orderType: string,
  items: AnalyticsOrderItem[],
  accounts: AnalyticsAccount[],
  source: OrderSource
): void {
  for (const item of items) {
    const venue = accounts.find((a) => a.accountId === item.accountId)?.tradeVenue
    const size = Number(item.quantity)
    void track('Submit Order', {
      order_type: orderType,
      exchange: tradeVenueName(venue),
      instrument: item.symbol,
      size: Number.isFinite(size) ? size : 0,
      size_denomination: denominationToJSON(item.denomination)
        .replace('DENOMINATION_', '')
        .toLowerCase(),
      source,
    })
  }
}

/** Limit/market batch: one event per order in the batch. */
export function trackSubmittedOrders(
  orders: NewOrderParams[],
  accounts: AnalyticsAccount[],
  source: OrderSource
): void {
  for (const order of orders) {
    trackOrderItems(orderTypeName(order.orderType), [order], accounts, source)
  }
}
